import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import common from 'constants/common';
import BaseButton from 'components/atoms/baseButton';
import useUser from 'utils/useUser';
import { useCheckRoleAccess } from 'infrastructure/hooks';
import { PlusOutlined } from '@ant-design/icons';

import s from './styles.module.scss';

interface InteractiveTimeCardProps {
  ccmClinicTime?: number;
  ccmTotalTime?: number;
  rpmClinicTime?: number;
  rpmTotalTime?: number;
  clinicReviewTimeSeconds?: number;
  currentMonth?: string;
  addLoading?: boolean;
  onAdd?: () => void;
}

const InteractiveTimeCard: FC<InteractiveTimeCardProps> = ({
  ccmClinicTime = 0,
  ccmTotalTime = 0,
  rpmClinicTime = 0,
  rpmTotalTime = 0,
  clinicReviewTimeSeconds = 0,
  currentMonth,
  addLoading,
  onAdd,
}) => {
  const { t } = useTranslation();

  const { isPractice, isAdmin } = useUser();
  const { isReadOnlyRole } = useCheckRoleAccess();

  const today = new Date();
  const monthLabel =
    currentMonth ||
    format(today, common.dateFormats?.interactiveTime || 'MMMM');

  const formatSeconds = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
      2,
      '0',
    )}:${String(secs).padStart(2, '0')}`;
  };

  const clinicReviewMinutes = Math.floor(clinicReviewTimeSeconds / 60);

  return (
    <div className={s['interactive-time-card']}>
      <div className={s['card-header']}>
        <h4 className={s['card-title']}>{t('controls.interactiveTime')}</h4>
        <span className={s['card-month']}>{monthLabel}</span>
      </div>

      <div className={s['card-content']}>
        <div className={s['time-section']}>
          <div className={s['section-header']}>
            <span className={s['module-label-rpm']}>RPM</span>
            <div className={s['time-values']}>
              <div className={s['time-row']}>
                <span className={s['time-label']}>Clinic</span>
                <span className={s['time-value']}>
                  {formatSeconds(rpmClinicTime)}
                </span>
              </div>
              <div className={s['time-row']}>
                <span className={s['time-label']}>Total</span>
                <span className={s['time-value']}>
                  {formatSeconds(rpmTotalTime)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className={s.divider} />

        <div className={s['time-section']}>
          <div className={s['section-header']}>
            <span className={s['module-label-ccm']}>CCM</span>
            <div className={s['time-values']}>
              <div className={s['time-row']}>
                <span className={s['time-label']}>Clinic</span>
                <span className={s['time-value']}>
                  {formatSeconds(ccmClinicTime)}
                </span>
              </div>
              <div className={s['time-row']}>
                <span className={s['time-label']}>Total</span>
                <span className={s['time-value']}>
                  {formatSeconds(ccmTotalTime)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className={s.divider} />

        <div className={s['review-section']}>
          <span className={s['review-label']}>Clinic Review Time</span>
          <span className={s['review-value']}>
            {clinicReviewMinutes} minutes
          </span>
        </div>

        <div className={s['combined-btn-wrapper']}>
          {(isPractice || isAdmin) && (
            <BaseButton
              label={t('labels.addTime')}
              dataCy="add-manual-time-button"
              icon={<PlusOutlined />}
              type="secondary"
              onClick={onAdd}
              loading={addLoading}
              disabled={isReadOnlyRole || addLoading}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default InteractiveTimeCard;
